import type { BuilderComponent } from 'hb-builder/types';
import { documentsIdsND } from 'hb-builder/utils/migrate/schemas/data/documentsIdsND';
import { needMigration } from 'hb-builder/utils/migrate/schemas/pageBuilder';
import type { CommonSchemaBuilder } from 'hb-builder/utils/migrate/schemas/types';
import { getNewButton } from 'hb-builder/utils/migrate/utils/getNewButton';

const replaceQlClasses = (html: string) => {
  const div = document.createElement('div');

  div.innerHTML = html;

  Array.from(div.querySelectorAll<HTMLElement>('.ql-align-center')).forEach(
    (el) => {
      el.classList.remove('ql-align-center');

      if (el.classList.length === 0) {
        el.removeAttribute('class');
      }

      el.style.textAlign = 'center';
    },
  );

  return div.innerHTML;
};

const removeExcessiveLineBreaks = (html: string) => {
  const regex = /<p><br><\/p><p><br><\/p><p><br><\/p><p><br><\/p><p><br><\/p>/g;
  const lineBreaksFound = html.match(regex);

  let finalHtml = html;

  if (lineBreaksFound && lineBreaksFound?.length >= 1) {
    finalHtml = html.replace(regex, '');
  }

  return finalHtml;
};

const schemas = [
  ({ document }: CommonSchemaBuilder<BuilderComponent>) => {
    // Fix bug introduced by line break. Some quill editor classes have been added.
    if (needMigration(document, '2021-01-25')) {
      return {
        Text: {
          content: replaceQlClasses,
        },
      };
    }

    return null;
  },

  ({ document }: CommonSchemaBuilder<BuilderComponent>) => {
    if (needMigration(document, '2021-02-09')) {
      return {
        Section: {
          children: (sectionChildren) =>
            sectionChildren.map((column) => ({
              ...column,
              attributes: {
                ...column.attributes,
                containerPadding:
                  column.attributes?.containerPadding || '0px 0px 0px 0px',
                // put an explicit width on the column
                ...(!column.attributes?.width ||
                parseInt(column.attributes?.width, 10) === 100
                  ? {
                      width: `${100 / sectionChildren.length}%`,
                    }
                  : {
                      width:
                        typeof column.attributes?.width === 'number'
                          ? `${column.attributes?.width}%`
                          : column.attributes?.width,
                    }),
              },
            })),
        },
      };
    }

    return null;
  },

  ({ document }: CommonSchemaBuilder<BuilderComponent>) => {
    // Fix bug with multiple line breaks
    if (needMigration(document, '2021-03-11')) {
      return {
        Text: {
          content: removeExcessiveLineBreaks,
        },
      };
    }

    return null;
  },

  ({ document }: CommonSchemaBuilder<BuilderComponent>) => {
    // Add column to Hero component
    if (needMigration(document, '2021-04-01')) {
      return {
        Hero: {
          children: (heroChildren) => {
            if (heroChildren[0].type === 'Column') {
              return [
                {
                  ...heroChildren[0],
                  attributes: {
                    ...heroChildren[0].attributes,
                    disableSelection: true,
                  },
                },
              ];
            }

            return [
              {
                type: 'Column',
                children: heroChildren,
                attributes: {
                  disableSelection: true,
                  height: '100%',
                  verticalAlign: 'middle',
                  width: '100%',
                },
              },
            ];
          },
        },
      };
    }

    return null;
  },

  ({ document }: CommonSchemaBuilder<BuilderComponent>) => {
    // Add horizontal alignment default value to Hero component
    if (needMigration(document, '2021-12-31')) {
      return {
        Hero: {
          attributes: () => ({
            horizontalAlign: () => 'center',
          }),
        },
      };
    }

    return null;
  },

  ({ document }: CommonSchemaBuilder<BuilderComponent>) => {
    // Remove unused theme attribute
    if (needMigration(document, '2022-02-25')) {
      return {
        Section: {
          theme: () => null,
        },
      };
    }

    return null;
  },

  ({ document, config }: CommonSchemaBuilder<BuilderComponent>) => {
    // Add missing body attributes
    if (needMigration(document, '2022-07-01')) {
      return {
        Body: {
          attributes: (attr) => ({
            bodyWidth: () => {
              if (!attr.bodyWidth) {
                return config?.body?.defaultWidth;
              }

              return attr.bodyWidth;
            },
            bodyBackgroundColor: () => {
              if (!attr.bodyBackgroundColor) {
                return config?.body?.defaultColor;
              }

              return attr.bodyBackgroundColor;
            },
            displayFooter: () => {
              if (config?.footer?.displayFooter) {
                return true;
              }

              return null;
            },
          }),
        },
      };
    }

    return null;
  },
  ({ document }: CommonSchemaBuilder<BuilderComponent>) => {
    // Add category to all single components
    if (needMigration(document, '2023-10-01')) {
      return {
        Button: {
          category: () => 'single',
        },
        Divider: {
          category: () => 'single',
        },
        Html: {
          category: () => 'single',
        },
        Spacer: {
          category: () => 'single',
        },
        Text: {
          category: () => 'single',
        },
      };
    }

    return null;
  },
  ({ document }: CommonSchemaBuilder<BuilderComponent>) => {
    // For ND only: fix font color on text component - https://hivebrite.atlassian.net/browse/PS-8359
    const isNotreDame = !!documentsIdsND.find((id) => id === document.id);

    if (isNotreDame && needMigration(document, '2024-02-01')) {
      return {
        Text: {
          content: (textContent, item) => {
            // If we need to always render the default text color from the theme,
            // or if there's no textColor attribute specified,
            // we should not update its style
            if (
              !item.attributes ||
              item.attributes.defaultTextColor ||
              !item.attributes.textColor
            ) {
              return textContent;
            }

            let newContentWithColor = '';

            const paragraphs = textContent.split('</p>');
            const pSpanWithoutColorStyle =
              /(<p>|<p style=".*?">)(<span style="(((?!.*color:.*?).*?))">)/g;

            paragraphs.forEach((paragraph) => {
              if (paragraph.length) {
                // If there's a span following the p tag, we need to update its style (expect if there's already a color)
                if (pSpanWithoutColorStyle.test(paragraph)) {
                  const p = `${paragraph}</p>`;

                  newContentWithColor += p.replaceAll(
                    pSpanWithoutColorStyle,
                    `$1<span style="$4; color: ${item.attributes.textColor}">`,
                  );
                } else {
                  const p = `${paragraph}</span></p>`;

                  // If there's no existing span after the p tag, we can just add it and set a text color
                  newContentWithColor += p.replaceAll(
                    /(<p>|<p style=".*?">)/g,
                    `$1<span style="color: ${item.attributes.textColor}">`,
                  );
                }
              }
            });

            return newContentWithColor;
          },
        },
      };
    }

    return null;
  },

  ({ document, theme }: CommonSchemaBuilder<BuilderComponent>) => {
    if (!theme || !theme.colors || !theme.themeVariables || !theme.buttons) {
      return null;
    }

    if (needMigration(document, '2024-03-31')) {
      return {
        Column: {
          children: (children) =>
            children.map((child) => {
              if (child.type === 'Button') {
                const mode = child.attributes.mode || 'custom';
                const isCustom = mode === 'custom';

                return getNewButton(child, false, isCustom, theme);
              }

              return child;
            }),
        },
      };
    }

    return null;
  },

  ({ document }: CommonSchemaBuilder<BuilderComponent>) => {
    if (needMigration(document, '2025-04-31')) {
      return {
        Text: {
          attributes: () => ({
            horizontalAlign: () => undefined,
          }),
        },
      };
    }

    return null;
  },
];

export default schemas;
